import { debounce } from 'lodash';
import { type ComputedRef, type Ref, ref, watch } from 'vue';

import { SAVE_STATE, type SaveState } from '@/js/utils/constants';

interface Args<T> {
  value: ComputedRef<T>;
  save: (newValue: T) => Promise<unknown>;
  form?: Ref<HTMLFormElement | null>;
  debounce?: number;
}

export default function useFormFieldAutoSave<T>({
  value,
  save,
  form,
  debounce: debounceRate,
}: Args<T>) {
  const formValue = ref(value.value) as Ref<T>;
  const saving = ref<SaveState>(SAVE_STATE.INACTIVE);

  const update = async () => {
    if (form?.value && !form.value.checkValidity()) return;
    if (formValue.value === value.value) return;
    saving.value = SAVE_STATE.SAVING;
    if (await save(formValue.value)) {
      saving.value = SAVE_STATE.SAVED;
    } else {
      saving.value = SAVE_STATE.INACTIVE;
    }
  };

  const debounced = debounceRate ? debounce(update, debounceRate) : false;

  // Watch input value for changes, and save to API (optionally debounced)
  watch(formValue, debounced || update);
  // Watch store value for changes, and update input value if necessary
  watch(value, () => {
    if (value.value !== formValue.value) formValue.value = value.value;
  });

  return {
    update: debounced ? () => debounced.flush() : update,
    formValue,
    saving,
  };
}
